import dayjs from 'dayjs'
import _ from 'lodash'
import type React from 'react'
import { useCallback } from 'react'
import { browserName } from 'react-device-detect'
// @ts-ignore
import saveSvgAsPng from 'save-svg-as-png'
import { loadImageAsDataUrl } from './images'
import PdfExport from './PdfExport'
import { Header, LogListTable, PageNumbers, StoolTable } from './pdfContent'
import { tableStyles } from './styles'

type LogPictures = Record<string, unknown>

const withPictureDataUrls = async (log: LogPictures): Promise<LogPictures> => {
  const updatedLog: LogPictures = { ...log }

  for (let i = 1; i <= 3; i++) {
    const key = `picture${i}`
    const url = log[key]
    if (typeof url !== 'string') continue

    try {
      updatedLog[`${key}Base64`] = await loadImageAsDataUrl(url)
    } catch (err) {
      console.error(`Failed to load image: ${url}`, err)
    }
  }

  return updatedLog
}

interface ChartPdfExportProps {
  resident: any
  timeFormat: string
  title: string
  groupBy: string
  logs: any[]
  type: string
  dataUrl: string | null
  query: any
}

const ChartPdfExport: React.FC<ChartPdfExportProps> = ({
  resident,
  timeFormat,
  title,
  groupBy,
  logs,
  type,
  dataUrl,
  query
}) => {
  const SVGtoPNG = (svg: string): Promise<string> => {
    return new Promise((resolve) => {
      saveSvgAsPng.svgAsPngUri(svg, { scale: 3 }, (uri: string) => {
        resolve(uri)
      })
    })
  }

  const getDocDefinition = (
    image: string | null,
    getBody: any,
    fetchedLogs: any[]
  ) => {
    const filteredLogs = (fetchedLogs || logs).map((log: any) =>
      log.deleted
        ? { ...log, description: null, title: 'Log Removed' }
        : { ...log }
    )

    const _logs = _.sortBy(filteredLogs, (d) => -dayjs(d.timeLogged))

    let pages
    if (groupBy === 'date') {
      pages = _.chain(_logs)
        .groupBy(({ timeLogged }) => dayjs(timeLogged).format('DD MMM YYYY'))
        .map((group, date) => ({
          heading: date,
          logs: group,
          timeLogged: _.get(group, '0.timeLogged')
        }))
        .orderBy(({ timeLogged }) => -dayjs(timeLogged))
        .value()
    } else {
      pages = [{ logs: _.orderBy(_logs, (d) => -dayjs(d.timeLogged)) }]
    }

    return {
      content: pages.map((row, index) => {
        const chartImage = image
          ? {
              width: 500,
              margin: [0, 10, 0, 40],
              alignment: 'center',
              image
            }
          : null
        const rowTitle = `${resident.name.first} ${resident.name.last} - ${title}`
        return [
          // @ts-ignore
          Header({ title: rowTitle, date: row.heading }),
          chartImage,
          getBody(row, timeFormat),
          index + 1 !== pages.length ? { pageBreak: 'after', text: '' } : null
        ].filter(Boolean)
      }),
      footer: PageNumbers,
      styles: tableStyles
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing dependency list; changing when this re-runs needs behavioural review
  const prepareDocDefinition = useCallback(
    async (fetchedLogs: any[]) => {
      let image: string | null = null
      const chartElements = document.getElementsByClassName('highcharts-root')

      try {
        if (
          browserName &&
          browserName.match(/Chrome|Chromium|Edge|Firefox/) &&
          chartElements.length
        ) {
          image = await SVGtoPNG(chartElements?.[0] as unknown as string)
        }
      } catch (e) {
        console.error(`SVG to PNG error:`, e)
      }

      // Every photo is fetched past the browser cache, so a wide date range over
      // photo-heavy logs would otherwise start hundreds of requests at once.
      const logsWithBase64: LogPictures[] = []
      for (const batch of _.chunk(fetchedLogs || logs, LOG_BATCH_SIZE)) {
        logsWithBase64.push(
          ...(await Promise.all(batch.map(withPictureDataUrls)))
        )
      }

      return type === 'stool'
        ? getDocDefinition(image, StoolTable, logsWithBase64)
        : getDocDefinition(image, LogListTable, logsWithBase64)
    },
    [logs, type, groupBy, resident, title, timeFormat]
  )

  const getUrl = (url: string, query: any = {}) => {
    const { to, from } = query
    let result = `${url}?limit=10000`
    if (from) result += `&from=${from}`
    if (to) result += `&to=${to}`
    return result
  }

  return (
    <PdfExport
      getDocDefinition={prepareDocDefinition}
      // @ts-ignore
      dataUrl={dataUrl && getUrl(dataUrl, query)}
      pdfTitle={title}
      style={{ float: 'right' }}
    />
  )
}

const LOG_BATCH_SIZE = 5

export default ChartPdfExport
